import { validateNormalizedEventVersion } from '@borgee/agent-remote-protocol';
import { decodeAgentSnapshot } from '@borgee/agent-remote-protocol';
import { validSessionCapabilities } from '@borgee/plugin-sdk/bpp-normalized';
import { PROTOCOL_VERSION, isInteractionRequestedMessage, isInteractionResolvedMessage, isInteractionResponseMessage, isPublicAgentTimelineEvent, isPublicAgentNonTimelineEvent } from '@borgee/agent-remote-protocol';
import { redactInteractionRequest, redactInteractionResponse } from '@borgee/agent-provider-sdk';
/** Adapt only envelopes that validate in their original declared version. */
export function upgradeLegacyEnvelope(value) {
    if (!value || typeof value !== 'object')
        return value;
    const event = value;
    if (typeof event.protocolVersion !== 'string')
        return value;
    return validateNormalizedEventVersion(event.protocolVersion, event) ? { ...event, protocolVersion: PROTOCOL_VERSION } : undefined;
}
export function normalizeActivityFrame(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return;
    const frame = value;
    if (frame.type !== 'agent_activity' || typeof frame.channel_id !== 'string' || typeof frame.agent_id !== 'string' || typeof frame.task_id !== 'string' || typeof frame.protocol_version !== 'string' || typeof frame.sequence !== 'number')
        return;
    if (frame.timeline_epoch !== undefined && (typeof frame.timeline_epoch !== 'string' || !/^[A-Za-z0-9_:.@/-]{1,512}$/.test(frame.timeline_epoch)))
        return;
    if (frame.occurred_at !== undefined && (typeof frame.occurred_at !== 'number' || !Number.isSafeInteger(frame.occurred_at) || frame.occurred_at < 0 || !Number.isFinite(new Date(frame.occurred_at).getTime())))
        return;
    if (frame.shape !== 'normalized' || !validateNormalizedEventVersion(frame.protocol_version, frame.event) || (!frame.task_id && !frame.timeline_epoch) || !Number.isSafeInteger(frame.sequence) || frame.sequence < 0)
        return;
    if (!frame.event || typeof frame.event !== 'object' || Array.isArray(frame.event))
        return;
    if ('protocolVersion' in frame.event && frame.event.protocolVersion !== frame.protocol_version)
        return;
    let event = upgradeLegacyEnvelope(frame.event);
    const snapshot = event?.type === 'agent_snapshot' ? decodeAgentSnapshot(JSON.stringify(event)) : undefined;
    if (snapshot?.status === 'ok') {
        const parsed = snapshot.value;
        if (parsed.payload.id !== frame.agent_id || parsed.payload.providerId !== parsed.payload.runtimeInfo.providerId)
            return;
        event = { ...parsed, payload: { ...parsed.payload, pendingInteractions: parsed.payload.pendingInteractions.map(redactInteractionRequest) } };
    }
    else if (isInteractionRequestedMessage(event)) {
        if (event.payload.agentId !== frame.agent_id)
            return;
        event = { ...event, payload: { ...event.payload, request: redactInteractionRequest(event.payload.request) } };
    }
    else if (isInteractionResolvedMessage(event)) {
        if (event.payload.agentId !== frame.agent_id)
            return;
    }
    else if (isPublicAgentTimelineEvent(event)) {
        if (event.item.type === 'interaction')
            event = { ...event, item: { ...event.item, request: redactInteractionRequest(event.item.request), response: redactInteractionResponse(event.item.request, event.item.response) } };
    }
    else if (!isPublicAgentNonTimelineEvent(event))
        return;
    if (frame.session_control_binding_id !== undefined && (typeof frame.session_control_binding_id !== 'string' || !/^[A-Za-z0-9_:.@/-]{1,512}$/.test(frame.session_control_binding_id)))
        return;
    if (frame.session_capabilities !== undefined && (event.type !== 'runtime_updated' || !validSessionCapabilities(frame.session_capabilities)))
        return;
    return { type: 'agent_activity', shape: 'normalized', channel_id: frame.channel_id, agent_id: frame.agent_id, task_id: frame.task_id, sequence: frame.sequence,
        protocol_version: PROTOCOL_VERSION, event: event,
        ...(frame.occurred_at !== undefined ? { occurred_at: frame.occurred_at } : {}),
        ...(frame.timeline_epoch !== undefined ? { timeline_epoch: frame.timeline_epoch } : {}),
        ...(typeof frame.session_control_binding_id === 'string' ? { session_control_binding_id: frame.session_control_binding_id } : {}),
        ...(validSessionCapabilities(frame.session_capabilities) ? { session_capabilities: frame.session_capabilities } : {}), };
}
export function normalizeResolution(value) {
    const event = upgradeLegacyEnvelope(value);
    return isInteractionResolvedMessage(event) ? event : undefined;
}
export function normalizeResponse(value) {
    const event = upgradeLegacyEnvelope(value);
    return isInteractionResponseMessage(event) ? event : undefined;
}
/** Missing request provenance cannot justify retaining potentially sensitive answer values. */
export function redactPublicResolution(resolution, request) {
    const response = resolution.payload.response;
    if (request && request.kind === response.kind)
        return { ...resolution, payload: { ...resolution.payload, response: redactInteractionResponse(request, response) } };
    if (response.kind === 'question')
        return { ...resolution, payload: { ...resolution.payload, response: { ...response, answers: response.answers.map(({ questionId }) => ({ questionId, selectedValues: [], redacted: true })) } } };
    if (response.kind === 'form' && response.action === 'submit')
        return { ...resolution, payload: { ...resolution.payload, response: { ...response, values: {}, redactedFields: [...new Set([...response.redactedFields ?? [], ...Object.keys(response.values)])] } } };
    return resolution;
}
//# sourceMappingURL=normalized-activity.js.map